import { z } from 'zod/v4';

export type MetadataLabeledOption<TValue extends string> = {
  label: string;
  value: TValue;
};

type MetadataLabeledSchemaShape = Record<string, z.ZodLiteral<string>>;

export function getSchemaMetaLabel(schema: z.ZodType) {
  const label = schema.meta()?.label;
  if (typeof label !== 'string') {
    throw new Error('Expected schema metadata to include a label');
  }

  return label;
}

export function getMetadataLabeledOptions<TShape extends MetadataLabeledSchemaShape>(
  schema: z.ZodObject<TShape>
): Array<MetadataLabeledOption<z.infer<TShape[keyof TShape]>>> {
  return Object.entries(schema.shape).map(([value, valueSchema]) => ({
    label: getSchemaMetaLabel(valueSchema),
    value: valueSchema.parse(value) as z.infer<TShape[keyof TShape]>,
  }));
}

export function getMetadataLabeledOptionValues<TShape extends MetadataLabeledSchemaShape>(
  schema: z.ZodObject<TShape>
): [z.infer<TShape[keyof TShape]>, ...Array<z.infer<TShape[keyof TShape]>>] {
  const values = getMetadataLabeledOptions(schema).map((option) => option.value);
  if (values.length === 0) {
    throw new Error('Expected schema to include at least one option');
  }

  return values as [z.infer<TShape[keyof TShape]>, ...Array<z.infer<TShape[keyof TShape]>>];
}

export function getMetadataLabeledOptionLabel<TValue extends string>(
  options: Array<MetadataLabeledOption<TValue>>,
  value: TValue
) {
  return options.find((option) => option.value === value)?.label ?? value;
}
